import { get as lodashGet } from 'lodash';
import { getModel } from 'src/machete-redux';
import * as viewportUnitsBuggyfill from 'viewport-units-buggyfill';
import constants from 'src/constants';
import createDebug from 'debug';

const debug = createDebug('designPro.machete.utils');
const {
  MATERIALS,
  SASH_ID,
  GLASS_ID,
  BRICK_ID,
  DOOR_PACKAGE,
} = constants.labels;
const { SPLIT_MODES } = constants.app;

/*
 * Get an object with the values of the split properties associated
 * with this node (present, size, and orientation).
 * @param {Hover.SegmentationNode} node
 * @return {Object} Object with present, size, and orientation
 * values for the node
 */
const getSplitProperties = (node) => {
  const size = node.getProperty('size') || { value: 0 };
  const present = node.getProperty('present') || { value: false };
  const orientation = node.getProperty('orientation') || {
    value: SPLIT_MODES.HORIZONTAL,
  };
  return {
    size: size.value,
    present: present.value,
    orientation: orientation.value,
  };
};

const resizeViewer = () => {
  window.Hover.Machete.viewer.resize();
  // force expands bottom navbar for ios
  document.body.scrollIntoView();
  window.setTimeout(() => {
    viewportUnitsBuggyfill.refresh(true);
  }, 500);
};

/*
 * NOTE: utils in this file are candidates for moving to either:
 * 1) machete: https://github.com/hoverinc/machete
 * 2) machete-redux: https://github.com/hoverinc/machete-redux
 */

/*
 * Get the value of the primary label id from the node.
 * Return undefined if it does not exist.
 * @param {Hover.SegmentationNode} node
 * @return {number} label id
 */
const getPrimaryLabelId = (node) =>
  lodashGet(node.getProperty('primary_label_id'), 'value');

/*
 * Return whether or not the node is glass.
 * @param {Hover.SegmentationNode} node
 * @return {boolean}
 */
const isGlass = (node) => getPrimaryLabelId(node) === GLASS_ID;

/*
 * Return whether or not the node is a window (aka sash) or door.
 * @param {Hover.SegmentationNode} node
 * @return {boolean}
 */
/**
 * NOTE: PATIO_DOOR_ID, SLIDING_GLASS_DOOR_ID, etc will not exist on Segmentation
 * Tree nodes, only their annotation nodes will have these IDs. and the annotation
 * parent node will always use DOOR_PACKAGE. This UUID of the DOOR_PACKAGE
 * segmentation node will be the same as the UUID of the Door Feature that
 * corresponds to that node.
 */
const isWindowOrDoor = (node) =>
  [SASH_ID, DOOR_PACKAGE].includes(getPrimaryLabelId(node));

/*
 * @param {number}
 * @return {boolean}
 */
const isBrick = (labelId) => labelId === BRICK_ID;

/*
 * Get the window facade from a glass node.
 * Return undefined if it does not exist.
 * @param {Hover.SegmentationNode} node
 * @return {Hover.Facade} window facade
 */
const getFacadeFromGlassNode = (node) =>
  lodashGet(node, 'annotations[0].facade');

/*
 * Get the window node uuid from a glass node.
 * Return the glass node uuid if the window node is not found.
 *
 * This util is needed because 1) users cannot customize the glass of the model
 * and 2) the window node is difficult to click on (since it is smaller).
 * Therefore, when a glass node is selected, we actually select the surrounding
 * window node. See Jira NS-47 for more context.
 * @param {Hover.SegmentationNode} glassNode
 * @return {string} window node uuid
 */
const getNodeUuidFromGlassNode = (glassNode) => {
  const model = getModel();
  const glassFacade = getFacadeFromGlassNode(glassNode);
  const glassParentNode = model.segmentationTree.findNodeFromModelElement(
    glassFacade,
  );

  if (!isWindowOrDoor(glassParentNode)) return null;
  return glassParentNode.uuid;
};

/*
 * Add the repeatable property to products so that textures on
 * openings render correctly. This is a temporary solution and should
 * eventually be handled by the backend.
 * See NS-422 for more context.
 * @param {Object} products
 * @return {Object} updated products with repeatable property
 */
const addRepeatablePropertyToProducts = (products) =>
  Object.entries(products).reduce((acc, [id, product]) => {
    const updatedProduct = { ...product };
    if (!('repeatable' in product)) {
      const { label_id: labelId } = product;
      updatedProduct.repeatable = Hover.LabelUtils.isTextureRepeatable(labelId);
    }
    acc[id] = updatedProduct;
    return acc;
  }, {});

/*
 * nodes with primary_label_id that aren't pre-defined
 * in our labels constants shouldn't be clickable.
 */
const nodeHasValidPrimaryLabelId = ({
  properties: { primary_label_id = {} },
}) => !!MATERIALS[primary_label_id.value];

/*
 * In Machete, visible elements of the model are either parent nodes,
 * or another type of node that has `present` property === true.
 */
const nodeIsVisible = ({ type, properties }) =>
  type === 'parent' || (properties.present && properties.present.value);

/*
 * Not all products have a valid data for texture thumbnail URL. This util
 * returns a boolean value representing whether or not this texture can be used
 * as a machete node `texture_id`.
 * @param {Object} texture / product data
 * @return {Boolean} Whether the product has a valid texture image URL
 */
const isValidTexture = (texture = {}) =>
  !!lodashGet(texture, 'textureData.texture.thumbnail.url');

export default {
  addRepeatablePropertyToProducts,
  getPrimaryLabelId,
  getSplitProperties,
  getFacadeFromGlassNode,
  getNodeUuidFromGlassNode,
  isBrick,
  isGlass,
  isValidTexture,
  isWindowOrDoor,
  nodeHasValidPrimaryLabelId,
  nodeIsVisible,
  resizeViewer,
};
